import React, { useState, useEffect, useContext } from 'react';
import TableSortLabel from '@mui/material/TableSortLabel';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Container,
  CircularProgress,
  Box,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  TablePagination,
  FormControlLabel,
  Switch,
  InputAdornment,
  Alert,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import ClearIcon from '@mui/icons-material/Clear';
import AuthContext from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import OrganizationCreateWizard from '../components/OrganizationCreateWizard';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';

const TAB_VALUES = ['practices', 'labs'];
const getTabFromHash = (hash) => {
  const tab = (hash || '').replace('#', '').trim().toLowerCase();
  return TAB_VALUES.includes(tab) ? tab : null;
};

export default function Organizations() {
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const { tokens } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { phase2 } = useFeatureFlags();
  // Practice Type suggestions
  const practiceTypeOptions = [
    'Family Medicine', 'Internal Medicine', 'Pediatrics', 'Obstetrics & Gynecology',
    'General Practice', 'Geriatric Medicine', 'Emergency Medicine', 'Urgent Care Clinic/Center',
    'Nurse Practitioner (Primary Care)', 'Physician Assistant (Medical)',
    'Cardiology', 'Endocrinology Diabetes & Metabolism', 'Gastroenterology',
    'Hematology', 'Hematology & Oncology', 'Infectious Disease', 'Nephrology',
    'Pulmonary Disease', 'Rheumatology', 'Allergy & Immunology', 'Neurology',
    'General Surgery', 'Orthopedic Surgery', 'Urology', 'Critical Care Medicine'
  ];
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Search and pagination state
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [activeTab, setActiveTab] = useState(() => getTabFromHash(window.location.hash) || 'practices');
  // Debounce raw search input to prevent refocus on each keystroke
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchText(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  // Pagination handlers
  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  // Helper to copy corporate address into billing fields
  const handleCopyCorporateToBilling = () => {
    setEditData(prev => ({
      ...prev,
      street_1: prev.corporate_street_1 || '',
      street_2: prev.corporate_street_2 || '',
      city: prev.corporate_city || '',
      state: prev.corporate_state || '',
      zip_code: prev.corporate_zip_code || ''
    }));
  };
  // Helper to copy billing address into shipping fields
  const handleCopyBillingToShipping = () => {
    setEditData(prev => ({
      ...prev,
      shipping_street_1: prev.street_1 || '',
      shipping_street_2: prev.street_2 || '',
      shipping_city: prev.city || '',
      shipping_state: prev.state || '',
      shipping_zip_code: prev.zip_code || ''
    }));
  };
  // Helper to copy corporate address into shipping fields
  const handleCopyCorporateToShipping = () => {
    setEditData(prev => ({
      ...prev,
      shipping_street_1: prev.corporate_street_1 || '',
      shipping_street_2: prev.corporate_street_2 || '',
      shipping_city: prev.corporate_city || '',
      shipping_state: prev.corporate_state || '',
      shipping_zip_code: prev.corporate_zip_code || ''
    }));
  };
  // State for create organization wizard
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const isLabEdit = Boolean(editData?.is_lab);

  useEffect(() => {
    const tabFromHash = getTabFromHash(location.hash);
    if (!tabFromHash) {
      navigate(
        { pathname: location.pathname, search: location.search, hash: '#practices' },
        { replace: true }
      );
      return;
    }
    if (tabFromHash !== activeTab) {
      setActiveTab(tabFromHash);
      setPage(0);
    }
  }, [location.hash, location.pathname, location.search, navigate, activeTab]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedOrganizations = organizations.slice().sort((a, b) => {
    let aVal = a[orderBy] ?? '';
    let bVal = b[orderBy] ?? '';
    if (typeof aVal === 'string') {
      const cmp = aVal.localeCompare(bVal);
      return order === 'asc' ? cmp : -cmp;
    }
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });

  const fetchOrganizations = async () => {
    if (!tokens?.id_token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * rowsPerPage);
      params.append('limit', rowsPerPage);
      if (searchText) params.append('search_text', searchText);
      if (activeTab === 'practices') params.append('is_practice', 'true');
      if (activeTab === 'labs') params.append('is_lab', 'true');
      const res = await fetch(`${apiBase}/organizations?${params.toString()}`, {
        headers: { Authorization: `Bearer ${tokens.id_token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch organizations');
      const data = await res.json();
      const items = Array.isArray(data.items) ? data.items : [];
      const count = Number.isFinite(data.total) ? data.total : 0;
      // If current page is out of range after data changes, reset to first page and refetch
      if (items.length === 0 && count > 0 && page > 0) {
        setPage(0);
        return;
      }
      setOrganizations(items);
      setTotal(count);
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrganizations(); }, [tokens, page, rowsPerPage, searchText, activeTab]);


  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => {
            navigate({
              pathname: location.pathname,
              search: location.search,
              hash: `#${value}`,
            });
            setPage(0);
          }}
        >
          <Tab label="Practices" value="practices" />
          <Tab label="Labs" value="labs" />
        </Tabs>
      </Box>
      {/* Search and pagination controls */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <TextField
          size="small"
          label="Search Organizations"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="Search by name"
          sx={{ width: 300 }}
          InputProps={{
            endAdornment: searchInput && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        {activeTab === 'practices' ? (
          <Button variant="contained" color="primary" onClick={() => setCreateDialogOpen(true)}>
            Create Practice
          </Button>
        ) : (
          <Box sx={{ minWidth: 140 }} />
        )}
      </Box>
      <TableContainer component={Paper}>
        {error ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={2}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={2}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'id' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'id'}
                  direction={orderBy === 'id' ? order : 'asc'}
                  onClick={() => handleRequestSort('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'npi' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'npi'}
                  direction={orderBy === 'npi' ? order : 'asc'}
                  onClick={() => handleRequestSort('npi')}
                >
                  NPI
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'name' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              {activeTab === 'labs' ? (
                <TableCell>Shipping Address</TableCell>
              ) : (
                <>
                  <TableCell>Billing Address</TableCell>
                  <TableCell>Practice?</TableCell>
                  <TableCell>On-site collection?</TableCell>
                  <TableCell>Collection Site?</TableCell>
                </>
              )}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {sortedOrganizations.map((p) => {
              const shortId = String(p.id).split('-')[0];
              return (
                <TableRow key={p.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Tooltip title={p.id} arrow>
                        <Typography variant="body2" sx={{ cursor: 'default' }}>
                          {shortId}
                        </Typography>
                      </Tooltip>
                      <Tooltip title="Copy full ID" arrow>
                        <IconButton
                          size="small"
                          onClick={() => navigator.clipboard.writeText(p.id)}
                          sx={{ ml: 0.5 }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>{p.npi}</TableCell>
                  <TableCell>{p.name}</TableCell>
                  {activeTab === 'labs' ? (
                    <TableCell>
                      {`${p.shipping_street_1 || ''}${p.shipping_street_2 ? `, ${p.shipping_street_2}` : ''}${p.shipping_city ? `, ${p.shipping_city}` : ''}${p.shipping_state ? `, ${p.shipping_state}` : ''}${p.shipping_zip_code ? ` ${p.shipping_zip_code}` : ''}`.trim() || '-'}
                    </TableCell>
                  ) : (
                    <>
                      {/* Combined Address */}
                      <TableCell>
                        {`${p.street_1}${p.street_2 ? `, ${p.street_2}` : ''}, ${p.city}, ${p.state} ${p.zip_code}`}
                      </TableCell>
                      <TableCell>{p.is_practice ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{p.is_doing_in_house_collection ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{p.is_collection_site ? 'Yes' : 'No'}</TableCell>
                    </>
                  )}
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton
                        aria-label="edit"
                        size="small"
                        onClick={() => { setSaveError(null); setEditData(p); setEditDialogOpen(true); }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Users">
                      <IconButton
                        aria-label="users"
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={() => navigate(`/organizations/${p.id}/users`)}
                      >
                        <PeopleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          </Table>
        )}
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {/* Edit Practice Dialog */}
      {editDialogOpen && editData && (
        <Dialog
          open={editDialogOpen}
          onClose={() => { setEditDialogOpen(false); setSaveError(null); }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{isLabEdit ? 'Edit Lab' : 'Edit Practice'}</DialogTitle>
          <DialogContent dividers>
            {/* Organization Details */}
            <Typography variant="h6" gutterBottom>
              Organization Details
            </Typography>
            <TextField
              margin="dense"
              label="Name"
              fullWidth
              variant="standard"
              value={editData.name || ''}
              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
            />
            <TextField
              margin="dense"
              label="NPI"
              fullWidth
              variant="standard"
              value={editData.npi || ''}
              onChange={(e) => setEditData({ ...editData, npi: e.target.value })}
            />
            {/* Corporate Address */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, textAlign: 'left' }}>
              Corporate Address
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                margin="dense"
                label="Street Address 1"
                fullWidth
                variant="standard"
                value={editData.corporate_street_1 || ''}
                onChange={(e) => setEditData({ ...editData, corporate_street_1: e.target.value })}
              />
              <TextField
                margin="dense"
                label="Address Line 2"
                fullWidth
                variant="standard"
                value={editData.corporate_street_2 || ''}
                onChange={(e) => setEditData({ ...editData, corporate_street_2: e.target.value })}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  margin="dense"
                  label="City"
                  fullWidth
                  variant="standard"
                  value={editData.corporate_city || ''}
                  onChange={(e) => setEditData({ ...editData, corporate_city: e.target.value })}
                />
                <TextField
                  margin="dense"
                  label="State"
                  fullWidth
                  variant="standard"
                  value={editData.corporate_state || ''}
                  onChange={(e) => setEditData({ ...editData, corporate_state: e.target.value })}
                />
                <TextField
                  margin="dense"
                  label="Zip Code"
                  fullWidth
                  variant="standard"
                  value={editData.corporate_zip_code || ''}
                  onChange={(e) => setEditData({ ...editData, corporate_zip_code: e.target.value })}
                />
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right', mb: 2 }}>
              <Button variant="outlined" size="small" onClick={handleCopyCorporateToBilling}>
                Copy Corporate to Billing
              </Button>
            </Box>
            {/* Billing Address */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, textAlign: 'left' }}>
              Billing Address
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                margin="dense"
                label="Street Address 1"
                fullWidth
                variant="standard"
                value={editData.street_1 || ''}
                onChange={(e) => setEditData({ ...editData, street_1: e.target.value })}
              />
              <TextField
                margin="dense"
                label="Street Address 2"
                fullWidth
                variant="standard"
                value={editData.street_2 || ''}
                onChange={(e) => setEditData({ ...editData, street_2: e.target.value })}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  margin="dense"
                  label="City"
                  fullWidth
                  variant="standard"
                  value={editData.city || ''}
                  onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                />
                <TextField
                  margin="dense"
                  label="State"
                  fullWidth
                  variant="standard"
                  value={editData.state || ''}
                  onChange={(e) => setEditData({ ...editData, state: e.target.value })}
                />
                <TextField
                  margin="dense"
                  label="Zip Code"
                  fullWidth
                  variant="standard"
                  value={editData.zip_code || ''}
                  onChange={(e) => setEditData({ ...editData, zip_code: e.target.value })}
                />
              </Box>
            </Box>
            {/* Shipping Address */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, textAlign: 'left' }}>
              Shipping Address
            </Typography>
            <Box sx={{ textAlign: 'right', mb: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button variant="outlined" size="small" onClick={handleCopyBillingToShipping}>
                Copy Billing to Shipping
              </Button>
              <Button variant="outlined" size="small" onClick={handleCopyCorporateToShipping}>
                Copy Corporate to Shipping
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                margin="dense"
                label="Street Address 1"
                fullWidth
                variant="standard"
                value={editData.shipping_street_1 || ''}
                onChange={(e) => setEditData({ ...editData, shipping_street_1: e.target.value })}
              />
              <TextField
                margin="dense"
                label="Street Address 2"
                fullWidth
                variant="standard"
                value={editData.shipping_street_2 || ''}
                onChange={(e) => setEditData({ ...editData, shipping_street_2: e.target.value })}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  margin="dense"
                  label="City"
                  fullWidth
                  variant="standard"
                  value={editData.shipping_city || ''}
                  onChange={(e) => setEditData({ ...editData, shipping_city: e.target.value })}
                />
                <TextField
                  margin="dense"
                  label="State"
                  fullWidth
                  variant="standard"
                  value={editData.shipping_state || ''}
                  onChange={(e) => setEditData({ ...editData, shipping_state: e.target.value })}
                />
                <TextField
                  margin="dense"
                  label="Zip Code"
                  fullWidth
                  variant="standard"
                  value={editData.shipping_zip_code || ''}
                  onChange={(e) => setEditData({ ...editData, shipping_zip_code: e.target.value })}
                />
              </Box>
            </Box>
            {!isLabEdit && (
              <>
                {/* Tax and Practice Details */}
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, textAlign: 'left' }}>
                  Tax and Practice Details
                </Typography>
                <TextField
                    margin="dense"
                    label="DBA"
                    fullWidth
                    variant="standard"
                    value={editData.dba || ''}
                    onChange={(e) => setEditData({ ...editData, dba: e.target.value })}
                />
                <TextField
                    margin="dense"
                    label="State Tax ID"
                    fullWidth
                    variant="standard"
                    required
                    value={editData.state_tax_id || ''}
                    onChange={(e) => setEditData({ ...editData, state_tax_id: e.target.value })}
                />
                <TextField
                    margin="dense"
                    label="EIN"
                    fullWidth
                    variant="standard"
                    required
                    value={editData.ein || ''}
                    onChange={(e) => setEditData({ ...editData, ein: e.target.value })}
                />
                <TextField
                  margin="dense"
                  label="QuickBooks Customer ID"
                  fullWidth
                  variant="standard"
                  value={editData.qb_id || ''}
                  onChange={(e) => setEditData({ ...editData, qb_id: e.target.value })}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(editData.is_tax_exempt)}
                      onChange={(e) => setEditData({ ...editData, is_tax_exempt: e.target.checked })}
                    />
                  }
                  label="Is Tax Exempt"
                  sx={{ mt: 1 }}
                />
                {/* Practice Type Selection */}
                <TextField
                  margin="dense"
                  label="Practice Type"
                  fullWidth
                  variant="standard"
                  select
                  value={editData.practice_type || ''}
                  onChange={(e) => setEditData({ ...editData, practice_type: e.target.value })}
                >
                  {practiceTypeOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
                <Box mt={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(editData.is_practice)}
                        onChange={(e) =>
                          setEditData(prev => ({
                            ...prev,
                            is_practice: e.target.checked,
                            // If turning off practice, also turn off on-site collections
                            ...(e.target.checked ? {} : { is_doing_in_house_collection: false })
                          }))
                        }
                      />
                    }
                    label="Is Practice"
                  />
                  <Box sx={{ ml: 4 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(editData.is_doing_in_house_collection)}
                          onChange={(e) => setEditData({ ...editData, is_doing_in_house_collection: e.target.checked })}
                          disabled={!Boolean(editData.is_practice)}
                        />
                      }
                      label="On-site collections"
                    />
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(editData.is_collection_site)}
                        onChange={(e) => setEditData({ ...editData, is_collection_site: e.target.checked })}
                      />
                    }
                    label="Is Collection Site"
                  />
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setEditDialogOpen(false); setSaveError(null); }} variant="text" color="primary">Cancel</Button>
            <Button
              onClick={async () => {
                try {
                  const res = await fetch(`${apiBase}/organizations/${editData.id}`, {
                    method: 'PUT',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${tokens.id_token}`
                    },
                    body: JSON.stringify(editData)
                  });
                  if (!res.ok) {
                    // Throw status code for simplified error handling
                    throw new Error(res.status.toString());
                  }
                  // Consume response body
                  await res.json();
                  // Refresh list to pick up updated fields such as practice_type
                  await fetchOrganizations();
                  setEditDialogOpen(false);
                  setEditData(null);
                } catch (err) {
                  console.error(err);
                  // Distinguish network errors from HTTP errors by error name
                  const isNetworkError = err.name === 'TypeError';
                  const msg = isNetworkError
                    ? 'Failed to save document (network error)'
                    : `Failed to save document (${err.message})`;
                  setSaveError(msg);
                }
              }}
              variant="contained"
              color="primary"
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {/* Create Organization Wizard */}
      <OrganizationCreateWizard
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={(newOrg) => {
          fetchOrganizations();
          setCreateDialogOpen(false);
        }}
      />
      <Snackbar
        open={Boolean(saveError)}
        autoHideDuration={6000}
        onClose={() => setSaveError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSaveError(null)} severity="error" sx={{ width: '100%' }}>
          {saveError}
        </Alert>
      </Snackbar>
    </Container>
  );
}
